// src/context/AuthContext.js

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";

const API_STATUS_URL = `${process.env.REACT_APP_API_BASE_URL}/api/games/auth/status`;

// Define the Context with default values AND new setter functions
const AuthContext = createContext({
  isAuthenticated: false,
  loadingAuth: true,
  setIsAuthenticatedTrue: () => {}, // Function to set state to true
  setIsAuthenticatedFalse: () => {}, // Function to set state to false
});

// Define the Hook for consuming the context
export const useAuth = () => useContext(AuthContext);

// Define the Provider component
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loadingAuth, setLoadingAuth] = useState(true);

  // 💡 NEW: Function to force authentication state to true (e.g., after successful login)
  const setIsAuthenticatedTrue = useCallback(() => {
    setIsAuthenticated(true);
  }, []);

  // 💡 NEW: Function to force authentication state to false (e.g., after logout/error)
  const setIsAuthenticatedFalse = useCallback(() => {
    setIsAuthenticated(false);
  }, []);

  // Logic to check authentication status securely on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      setLoadingAuth(true);
      try {
        await axios.get(API_STATUS_URL, {
          withCredentials: true,
        });

        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setLoadingAuth(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Use useMemo to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      loadingAuth,
      setIsAuthenticatedTrue,
      setIsAuthenticatedFalse,
    }),
    [
      isAuthenticated,
      loadingAuth,
      setIsAuthenticatedTrue,
      setIsAuthenticatedFalse,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {loadingAuth ? (
        <div
          style={{
            padding: "40px",
            textAlign: "center",
            fontSize: "1.2rem",
            color: "#3498db",
            backgroundColor: "#121212",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          Checking authentication status...
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
